// src/encounter/lib/from-patient-assessment.ts
// A/P LOAD MAPPER (PROD-1297; v2 model PROD-1422). Inverse of to-patient-assessment.ts — rebuilds the
// DS AssessmentPlanCanvas v2 state (a flat `diagnoses: DxCard[]` + a top-level `orders: PlanOrder[]`
// where a MED's `dxIds` is its full multi-link set from rxDiagnosisCodesRequest[] and every other
// order's `dxIds` is its single parent link — PROD-1422) PLUS the two app-side
// side-maps that carry what the canvas drops, from a fetched PatientAssessment row:
//   • OrderPayloadMap (orderId → structured order payload)  — order codes/dosage/sub-tests/system
//   • DxCodingMap     (dxId    → preserved Dx coding)        — Dx code/display/system
//   • identifier      (the document id)                     — drives POST-vs-PUT on the next save
//
// The wire is authoritative-nested: each AssessmentPlan in assessmentPlanCondition[] is a Dx CARD;
// its childMedicalCode buckets (medication / radiology[lab+imaging] / procedures[cpt] / snomed) hold
// that Dx's orders, and the TOP-LEVEL referrals[] re-attach to their parent Dx via parentId. We
// PRESERVE the server-assigned id (medication.identifier · radiology.id · plan.identifier ·
// referral.id) in PlanOrder.id so a re-save round-trips, and narrow the full wire Code /
// CodeDisplayName / ProcedureCode back down to the minimal OrderCoding the side-map holds.
//
// Null/empty pa → empty canvas (POST on save). If no Dx carries isPrimary, order is left as-is
// (the canvas treats index 0 as primary). PURE — no I/O.
import type {
  AssessmentCoding,
  AssessmentPlan,
  AssessmentPlanMedication,
  AssessmentPlanVaccination,
  AssessmentRadiology,
  AssessmentReferral,
  CodeDisplayName,
  OmittedTestCoding,
  PatientAssessment,
  ProcedureCode,
} from '@/types/assessment-api';
import type { CodedOrderPayload, DxCodingMap, LabMemberLab, OrderCoding, OrderPayloadMap } from './order-payload';
import { SYNTHETIC_ID_PREFIX } from './order-payload';
import type { DxCard, OrderType, PlanOrder } from './to-patient-assessment';
import { parseOccurrenceRequests } from './medication-frequency';
import { ensureOnePrimary } from './dx-primary';
import { classifyRadiologyRow } from './order-category';
import { fromOrderLabs } from './lab-vendor';
import { readBillTo } from './lab-bill-to';
import { clinicalDisplayName } from '@/lib/clinical-display-name';
import { assessmentArticlesToEducation, type EducationArticle } from './to-education-article';

/**
 * Synthesize a STABLE, UNIQUE fallback id for a hydrated order row that lacks a server id, so
 * distinct id-less orders never collide on '' in the OrderPayloadMap (last-writer-wins) — and a
 * later removeOrderPayload('') can't nuke all of them. Keyed by the parent Dx id + bucket + the
 * order's index within its Dx bucket. The SYNTHETIC_ID_PREFIX marks it as client-only so the save
 * mapper does NOT echo it back as a server identifier (a never-persisted order still POSTs).
 */
function fallbackOrderId(dxId: string, bucket: string, index: number): string {
  return `${SYNTHETIC_ID_PREFIX}${dxId}-${bucket}-${index}`;
}

/** Wrap-Up status-header inputs derived from a fetched assessment (PROD-1410): whether orders have
 *  been sent + the total order count. Kept separate from the canvas rebuild — it reads the same wire
 *  but the status header doesn't need the full canvas. PURE. */
export interface AssessmentOrderStatus {
  /** Orders have been sent (drives the progress bar's 0.5 step + the Send-Orders ✓). */
  orderSent: boolean;
  /** Total orders across the encounter — every nested per-Dx order + top-level referrals. */
  orderCount: number;
}

/**
 * Derive the Wrap-Up order status from a fetched PatientAssessment. Mirrors Flutter
 * `wrap_up_billing_screen` — `orderCount = apData.orderCount + activeMeds` (Flutter sums the same
 * nested set) and `isOrderSent` drives the progress. React has no server `orderCount`, so count the
 * nested per-Dx buckets (procedures/meds/vaccinations/radiology[labs+imaging]) + top-level referrals.
 * The "sent" flag: the wire carries BOTH `hasOrderSent` and `isOrderSign` (KEY-RENAME) — treat either
 * truthy as sent (LIVE-SMOKE `GetAssessmentByPatientId` to confirm which the server sets — PROD-1410).
 */
export function assessmentOrderStatus(
  pa: PatientAssessment | null | undefined,
  opts: { distinct?: boolean } = {},
): AssessmentOrderStatus {
  if (!pa) return { orderSent: false, orderCount: 0 };
  const orderSent = !!(pa.hasOrderSent || pa.isOrderSign);
  // Frame v2 (Andrew's walk, 7 Oct: "Send orders (2)" for one order): ONE order linked to N diagnoses is stored as N
  // nested rows with distinct ids (the save fan-out, PROD-1801), so counting rows counts it N times. With `distinct`
  // the count is the orders the Plan shows — the same content fan-in the canvas uses. OFF keeps the row count.
  if (opts.distinct) return { orderSent, orderCount: patientAssessmentToCanvas(pa).orders.length };
  // Count only ACTIVE rows — a tombstoned (isDeleted) order is no longer sendable, and an inflated
  // badge would imply there are orders to send when there aren't (Codex P2). Applies to every bucket
  // AND the parent Dx (orders under a deleted diagnosis don't count).
  // Some order-row types declare `isDeleted`, some don't — read it defensively so the filter
  // typechecks across every bucket (a row without the field is treated as active).
  const active = (arr: ReadonlyArray<unknown> | null | undefined): number =>
    (arr ?? []).filter((x) => (x as { isDeleted?: unknown } | null)?.isDeleted !== true).length;
  const orderCount =
    (pa.assessmentPlanCondition ?? []).reduce((n, dx) => {
      if (!dx || dx.isDeleted === true) return n; // skip deleted diagnoses (their orders don't count)
      const c = dx.childMedicalCode;
      if (!c) return n;
      return (
        n +
        active(c.assessmentPlanProcedures) +
        active(c.assessmentPlanSnomed) +
        active(c.assessmentPlanMedication) +
        active(c.assessmentPlanVaccination) +
        active(c.radiologyOrderRequest)
      );
    }, 0) + active(pa.referrals);
  return { orderSent, orderCount };
}

/** The rebuilt canvas state + side-maps + the document identifier (POST-vs-PUT). */
export interface PatientAssessmentCanvasState {
  diagnoses: DxCard[];
  /** Top-level orders (v2 PROD-1422); a med's `dxIds` is its full multi-link set, others single-link. */
  orders: PlanOrder[];
  payloads: OrderPayloadMap;
  dxCoding: DxCodingMap;
  /** Patient-education articles (top-level, Dx-less; PROD-1454). Non-deleted rows only. */
  articles: EducationArticle[];
  /** Document identifier — null when the assessment doesn't exist yet (next save POSTs). */
  identifier: string | null;
  /**
   * The A/P section narrative (Flutter's commentButton). Read from `notePlainText` first, falling back
   * to `notes`; '' when absent. Must be fed back into the save context or it is destroyed — see the
   * `assessmentNote` note in to-patient-assessment.
   */
  assessmentNote: string;
  /**
   * The raw `notes` value — the Quill Delta the Flutter client authored. Seeds the rich-text
   * editor so formatting survives a round-trip, and is what the save writes back into `notes`.
   * `undefined` when the wire carried no delta (or only plain text).
   */
  assessmentNoteDelta?: string;
}

/** Narrow the wire Code/AssessmentCoding back to the minimal OrderCoding (drop nulls/extra fields). */
function narrowCode(c: AssessmentCoding | null | undefined): OrderCoding | undefined {
  if (!c) return undefined;
  const code = c.code ?? undefined;
  const displayName = c.display ?? c.Definition ?? undefined;
  if (code == null && displayName == null) return undefined;
  const out: OrderCoding = { code: code ?? '', displayName: displayName ?? '' };
  if (c.system != null) out.system = c.system;
  return out;
}

/** Narrow one wire omitted panel member (PROD-2632) back to OrderCoding. */
function narrowOmittedTest(c: OmittedTestCoding | null | undefined): OrderCoding | undefined {
  if (!c) return undefined;
  if (c.code == null && c.display == null) return undefined;
  const out: OrderCoding = { code: c.code ?? '', displayName: c.display ?? '' };
  if (c.system != null) out.system = c.system;
  return out;
}

/** Narrow the lab/imaging CodeDisplayName back to OrderCoding. */
function narrowCodeDisplayName(c: CodeDisplayName | null | undefined): OrderCoding {
  return {
    code: c?.code ?? '',
    displayName: c?.displayName ?? '',
    ...(c?.system != null ? { system: c.system } : {}),
  };
}

/** Narrow the CPT ProcedureCode (capital-S System) back to OrderCoding. */
function narrowProcedureCode(c: ProcedureCode | null | undefined): OrderCoding {
  return {
    code: c?.code ?? '',
    displayName: c?.displayName ?? c?.display ?? '',
    ...(c?.System != null ? { system: c.System } : {}),
  };
}

/**
 * A hydrated order minus its `dxIds` — the parent-link is attached by the caller (the single parent
 * Dx, v2 single-link). Kept as a local alias so each `from*` helper needn't repeat the derivation.
 */
type HydratedOrder = Omit<PlanOrder, 'dxIds'>;

/**
 * Resolve a medication's full `dxIds` link set (PROD-1422 — meds only) from its rxDiagnosisCodesRequest[].
 * Each rx entry's dxCode.code is mapped back to a Dx id via codeToDxId; unresolvable/deleted entries are
 * skipped. The parent Dx (the bucket the med nested into) is ALWAYS first (it's dxIds[0] on re-save, and
 * the med must nest back into the same bucket), then the other linked diagnoses, deduped. An empty/legacy
 * rx array collapses to [parentDxId] — identical to the old single-link behavior.
 *
 * AMBIGUITY GUARD (Codex Major): the wire keys rx links by CODE, not by Dx id, so a blank code or a code
 * shared by >1 Dx cannot reliably identify which Dx a link belongs to. Using it to add a CROSS-link would
 * invent a link the user never made (e.g. two `I10` diagnoses, a med linked only to the second → the code
 * resolves to the first). So `ambiguousCodes` (+ blank codes) are NEVER used to add a link. The parent link
 * is unaffected (it comes from the unambiguous wire nesting, seeded first). Trade-off: a genuine A+B-share-a-
 * code multi-link isn't restored — acceptable (rare; the user re-links) vs. fabricating an unselected link.
 *
 * HOW THE LINKS ACTUALLY ROUND-TRIP (PROD-1428, Flutter parity): neither read endpoint returns
 * rxDiagnosisCodesRequest (the BE writes RxDiagnosisCodes only at sign, against PatientRxOrder), so on reload
 * this usually yields [parentDxId]. The extra links come back through NESTING instead: the save mapper fans
 * the med out under every linked Dx, and the content fan-in in patientAssessmentToCanvas (pushOrder) adds each
 * further copy's parent to the order's dxIds, the same way Flutter's PatientAssessment.medications() merges
 * copies by title (patient_assessment_model.dart:489-507). This rx path stays as a harmless extra source.
 */
function medDxIds(
  med: AssessmentPlanMedication,
  parentDxId: string,
  codeToDxId: ReadonlyMap<string, string>,
  ambiguousCodes: ReadonlySet<string>,
): string[] {
  const seen = new Set<string>([parentDxId]);
  const ids: string[] = [parentDxId];
  for (const link of med.rxDiagnosisCodesRequest ?? []) {
    if (link?.isDeleted === true) continue;
    const code = link?.dxCode?.code;
    if (code == null || code === '') continue; // blank code can't identify a Dx
    if (ambiguousCodes.has(code)) continue; // shared by >1 Dx — don't guess (would invent a link)
    const id = codeToDxId.get(code);
    if (id != null && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Read a persisted order note SAFELY. `notes`/`note` is a DUAL-ENCODED field across the Scrips apps:
 * some rows hold plain text, some hold a Quill delta (base_ap_item_add_widget.dart:234-261 renders it
 * through QuillStaticView, and src/dashboard/quill-delta.ts records that the backend `notes` convention
 * is a bare Quill ops array). PM and the order-request surfaces can both write one.
 *
 * Prefer `notePlainText` — which exists on the wire and was never read — then fall back. If all we have
 * IS a delta, flatten it rather than showing raw JSON, because the old behaviour was worse than ugly:
 * the JSON went into the edit dialog's plain TextArea and got RE-SAVED, laundering it into the record
 * (PROD-1801).
 */
function readOrderNote(
  note: string | null | undefined,
  notePlainText?: string | null,
): string | undefined {
  const plain = notePlainText?.trim();
  if (plain) return notePlainText as string;
  const raw = note?.trim();
  if (!raw) return undefined;
  if (!raw.startsWith('[') && !raw.startsWith('{')) return note as string;
  try {
    const parsed = JSON.parse(raw) as unknown;
    const ops = Array.isArray(parsed)
      ? parsed
      : Array.isArray((parsed as { ops?: unknown[] }).ops)
        ? (parsed as { ops: unknown[] }).ops
        : null;
    if (!ops) return note as string;
    const text = ops
      .map((op) => (typeof (op as { insert?: unknown }).insert === 'string' ? (op as { insert: string }).insert : ''))
      .join('')
      .trim();
    return text || undefined;
  } catch {
    return note as string; // not JSON after all — it was prose that happened to start with a bracket
  }
}

/** Build a PlanOrder + its side-map payload from one medication wire row. */
function fromMedication(
  med: AssessmentPlanMedication,
  fallbackId: string,
): {
  order: HydratedOrder;
  payload: CodedOrderPayload;
} {
  const medicationCode = narrowCode(med.medicationCode) ?? { code: '', displayName: '' };
  const payload: Extract<CodedOrderPayload, { kind: 'medication' }> = {
    kind: 'medication',
    medicationCode,
  };
  const route = narrowCode(med.route);
  if (route) payload.route = route;
  if (med.frequency != null) payload.frequency = med.frequency;
  // Occurrence inputs (quantity / timesADay / daysInterval / specificDays) round-trip out of the
  // wire occurenceRequest[] so a reloaded med rebuilds the same schedule on the next save (PROD-1340).
  const occ = parseOccurrenceRequests(med.occurenceRequest, med.frequency);
  if (occ.quantity != null) payload.quantity = occ.quantity;
  if (occ.timesADay != null) payload.timesADay = occ.timesADay;
  if (occ.daysInterval != null) payload.daysInterval = occ.daysInterval;
  if (occ.specificDays && occ.specificDays.length > 0) payload.specificDays = occ.specificDays;
  if (med.onSetDate != null) payload.startDate = med.onSetDate;
  // The server's order stamp, kept so the save sends it back unchanged (PROD-2854) — else the next
  // save re-stamps the medication with the time of that save. Flutter: AssessmentPlanMedication.fromJson.
  if (med.orderDate) payload.orderDate = med.orderDate;
  if (med.duration != null && med.duration !== '') {
    const n = Number(med.duration);
    if (!Number.isNaN(n)) payload.duration = n;
  }
  if (med.durationUnit != null) payload.durationUnit = med.durationUnit;
  // BE-required dose fields must round-trip (PROD-1339) — else a reloaded med re-saves them null
  // and the BE 400s ("Dose unit is missing !") on the next autosave (Codex CRITICAL).
  if (med.unitOfMeasurement != null) payload.doseUnit = med.unitOfMeasurement;
  if (med.dispense != null) payload.dispense = med.dispense;
  if (med.dispenseUnit != null) payload.dispenseUnit = med.dispenseUnit;
  if (med.refill != null) payload.refill = med.refill;
  if (med.substitution != null) payload.substitution = med.substitution;
  if (med.sig != null) payload.sig = med.sig;
  // Preserve persisted interaction alerts (see order-payload's vidalAlerts).
  if (Array.isArray(med.vidalAlertRequest) && med.vidalAlertRequest.length > 0) {
    payload.vidalAlerts = med.vidalAlertRequest;
  }
  const medNote = readOrderNote(med.notes, med.notePlainText);
  if (medNote != null) payload.note = medNote;
  // weight/priority (frequency `now`) round-trip out of the JSON-encoded instruction.
  if (med.instruction) {
    try {
      const info = JSON.parse(med.instruction) as { weight?: number; priority?: string };
      if (typeof info.weight === 'number') payload.weight = info.weight;
      if (info.priority) payload.priority = info.priority;
    } catch {
      /* non-JSON instruction (legacy free text) — ignore */
    }
  }

  const id = med.identifier ?? med.orderId ?? fallbackId;
  return {
    order: { id, type: 'medication', label: medicationCode.displayName },
    payload,
  };
}

const FULFILMENT_MODES = ['ContractedLab', 'PatientChoice'] as const;

/** "ContractedLab" | "PatientChoice" in that spelling (any casing in), else undefined. */
function readFulfilmentMode(v: unknown): 'ContractedLab' | 'PatientChoice' | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim().toLowerCase();
  return FULFILMENT_MODES.find((m) => m.toLowerCase() === t);
}

const idOf = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/**
 * PROD-2861: copies the saved fulfilmentMode / labPriceLineId / memberLabs / labItemId onto the lab payload,
 * and rebuilds `orderSearch` (the item the popup reopens) from labItemId, else the panel the order names.
 * A legacy row (no mode) is left as it was, so it keeps reopening in the legacy popup.
 */
export function applySavedFulfilment(lab: Extract<CodedOrderPayload, { kind: 'lab' }>, rad: AssessmentRadiology): void {
  const mode = readFulfilmentMode(rad.fulfilmentMode);
  if (!mode) return;
  lab.fulfilmentMode = mode;
  const line = idOf(rad.labPriceLineId);
  if (line) lab.labPriceLineId = line;
  const members: LabMemberLab[] = (Array.isArray(rad.memberLabs) ? rad.memberLabs : []).flatMap((m) => {
    const code = m && typeof m.code === 'string' ? m.code.trim() : '';
    const memberMode = readFulfilmentMode(m?.fulfilmentMode);
    if (!code || !memberMode) return [];
    return [
      {
        code,
        ...(m.system ? { system: m.system } : {}),
        fulfilmentMode: memberMode,
        ...(idOf(m.labVendorId) ? { labVendorId: idOf(m.labVendorId) } : {}),
        ...(idOf(m.labVendorName) ? { labVendorName: idOf(m.labVendorName) } : {}),
        ...(idOf(m.labPriceLineId) ? { labPriceLineId: idOf(m.labPriceLineId) } : {}),
      },
    ];
  });
  if (members.length > 0) lab.memberLabs = members;
  const itemId = idOf(rad.labItemId) ?? lab.panelDefinitionId;
  if (idOf(rad.labItemId)) lab.labItemId = idOf(rad.labItemId);
  if (!itemId) return;
  const nameOf = (code: string) =>
    lab.subTests.find((t) => t.code.trim().toLowerCase() === code.toLowerCase())?.displayName?.trim() || code;
  const patientsChoiceTests =
    mode === 'PatientChoice' ? [] : members.filter((m) => m.fulfilmentMode === 'PatientChoice').map((m) => nameOf(m.code));
  lab.orderSearch = {
    itemId,
    // The popup writes memberLabs for every ticked test of a clinic panel, and a panel id for a Panel.
    type: members.length > 0 ? 'Clinic panel' : lab.panelDefinitionId ? 'Panel' : 'Single test',
    ...(mode === 'PatientChoice' ? { patientsChoice: true } : {}),
    ...(patientsChoiceTests.length ? { patientsChoiceTests } : {}),
  };
}

/** Build a PlanOrder + payload from one radiology wire row (lab or imaging). */
function fromRadiology(
  rad: AssessmentRadiology,
  fallbackId: string,
): { order: HydratedOrder; payload: CodedOrderPayload } {
  // Classify by code like Flutter, still accepting the legacy React 'lab'/'radiology' rows
  // (PROD-2619). A Flutter-saved lab (108252007) must come back a lab WITH its sub-tests.
  const isLab = classifyRadiologyRow(rad) === 'lab';
  const code = narrowCodeDisplayName(rad.code);
  const type: OrderType = isLab ? 'lab' : 'imaging';
  let payload: CodedOrderPayload;
  if (isLab) {
    const subTests = (rad.labOrderValueSetTestDetails ?? [])
      .map(narrowCode)
      .filter((c): c is OrderCoding => c != null);
    const lab: Extract<CodedOrderPayload, { kind: 'lab' }> = { kind: 'lab', code, subTests };
    // PROD-2632: a signed lab returns its deselected members ([] = none). Kept on the payload so the
    // dialog reopens with them deselected and a re-save sends them again. `null` (unsigned/unknown)
    // derives nothing: the selection stays the snapshot (labOrderValueSetTestDetails) and no list is sent.
    if (Array.isArray(rad.omittedTests)) {
      lab.omittedTests = rad.omittedTests
        .map(narrowOmittedTest)
        .filter((c): c is OrderCoding => c != null);
    }
    // PROD-2640 (Patient #1001): the members another lab on the plan already orders, so the edit
    // dialog reopens them as "Already ordered in X" (not as left out) and a re-save sends them again.
    if (Array.isArray(rad.alreadyOrderedTests) && rad.alreadyOrderedTests.length > 0) {
      lab.alreadyOrderedTests = rad.alreadyOrderedTests
        .map(narrowOmittedTest)
        .filter((c): c is OrderCoding => c != null);
    }
    // PROD-2640 (Patient #1000): the saved Bill to, so the edit dialog reopens on it.
    const billTo = readBillTo(rad.billTo);
    if (billTo) lab.billTo = billTo;
    // PROD-2849: the saved answers to the lab's order questions (PROD-2640 P-B6), so a re-save keeps them.
    if (Array.isArray(rad.aoeAnswers)) {
      const answers = rad.aoeAnswers.flatMap((a) =>
        a && typeof a.questionText === 'string' && a.questionText.trim() && typeof a.value === 'string' && a.value.trim()
          ? [
              {
                questionText: a.questionText,
                value: a.value,
                ...(a.questionCode ? { questionCode: a.questionCode } : {}),
                ...(a.answerType ? { answerType: a.answerType } : {}),
              },
            ]
          : [],
      );
      if (answers.length > 0) lab.aoeAnswers = answers;
    }
    // PROD-2653: the saved performing lab, so the edit dialog reopens on it and a re-save keeps it.
    const vendor = fromOrderLabs(rad.orderLabs);
    if (vendor) lab.vendor = vendor;
    // PROD-2640: the catalogue v2 version the order was placed on, so the edit dialog reopens on
    // that exact version and a re-save keeps it. Absent / null = a legacy order.
    const panelId = typeof rad.panelDefinitionId === 'string' ? rad.panelDefinitionId.trim() : '';
    if (panelId) {
      lab.panelDefinitionId = panelId;
      if (typeof rad.panelVersion === 'number') lab.panelVersion = rad.panelVersion;
    }
    // PROD-2861 (Patient #1028): the lab model choices every save now stores, so an order placed in the
    // Add lab popup reopens there on its lab (or Patient's choice) after a reload, and the next save (the
    // sign included) sends them again. Never a cost.
    applySavedFulfilment(lab, rad);
    const labNote = readOrderNote(rad.note, rad.notePlainText);
    if (labNote != null) lab.note = labNote;
    payload = lab;
  } else {
    const img: Extract<CodedOrderPayload, { kind: 'imaging' }> = { kind: 'imaging', code };
    const imgNote = readOrderNote(rad.note, rad.notePlainText);
    if (imgNote != null) img.note = imgNote;
    payload = img;
  }
  return { order: { id: rad.id ?? fallbackId, type, label: code.displayName }, payload };
}

/** Build a PlanOrder + payload from one procedure AssessmentPlan (cpt or snomed). */
function fromProcedurePlan(
  plan: AssessmentPlan,
  system: 'cpt' | 'snomed',
  fallbackId: string,
): { order: HydratedOrder; payload: CodedOrderPayload } {
  const code =
    system === 'cpt' ? narrowProcedureCode(plan.procedureCode) : narrowCode(plan.snomedCode) ?? { code: '', displayName: '' };
  const payload: Extract<CodedOrderPayload, { kind: 'procedure' }> = {
    kind: 'procedure',
    system,
    code,
  };
  const procNote = readOrderNote(plan.notes, plan.notePlainText);
  if (procNote != null) payload.note = procNote;
  // ADD-TO-BILLING rides `isPrimary` on a procedure plan (patient_assessment_model.dart:725). Reading it
  // back is what makes the edit dialog reopen TICKED — assessment-plan-view read a payload field that was
  // always undefined after a reload, because the save wrote false and the load never looked (PROD-1801).
  if (plan.isPrimary === true) payload.addToBilling = true;
  // The plan's dates, kept so the save sends them back (PROD-2854). The server keeps the procedure's
  // ORDER date in `endDate` and re-stamps it with `now` whenever the request leaves it empty.
  if (plan.onSetDate) payload.onSetDate = plan.onSetDate;
  if (plan.endDate) payload.endDate = plan.endDate;
  return {
    order: { id: plan.identifier ?? fallbackId, type: 'procedure', label: code.displayName },
    payload,
  };
}

/** Build a PlanOrder + payload from one top-level referral wire row. */
function fromReferral(
  ref: AssessmentReferral,
  fallbackId: string,
): { order: HydratedOrder; payload: CodedOrderPayload } {
  const payload: Extract<CodedOrderPayload, { kind: 'referral' }> = { kind: 'referral' };
  const target =
    ref.referral && typeof ref.referral === 'object' && 'target' in ref.referral
      ? (ref.referral as { target?: unknown }).target
      : undefined;
  if (typeof target === 'string') payload.target = target;
  const additional = (ref.otherDiagnosis ?? [])
    .map((d) =>
      d.dxCode
        ? {
            code: d.dxCode.code ?? '',
            displayName: d.dxCode.displayName ?? '',
            ...(d.dxCode.system != null ? { system: d.dxCode.system } : {}),
          }
        : undefined,
    )
    .filter((c): c is OrderCoding => c != null);
  if (additional.length > 0) payload.additionalDiagnoses = additional;
  const refNote = readOrderNote(ref.notes, ref.notePlainText);
  if (refNote != null) payload.note = refNote;
  // Dates round-trip, as in Flutter's AssessmentReferral (PROD-2854).
  if (ref.onSetDate) payload.onSetDate = ref.onSetDate;
  if (ref.endDate) payload.endDate = ref.endDate;
  // PROD-1395: the role, the practitioner and the display round-trip; status/rootId are the server's, kept so a re-save
  // keeps the row (never sent).
  if (ref.performerRole) payload.performerRole = ref.performerRole;
  if (ref.targetPractitionerId) payload.targetPractitionerId = ref.targetPractitionerId;
  if (ref.targetDisplay) payload.targetDisplay = ref.targetDisplay;
  // PROD-3336: the speciality round-trips too.
  if (ref.targetSpecialityId) payload.targetSpecialityId = ref.targetSpecialityId;
  if (ref.targetSpecialityName) payload.targetSpecialityName = ref.targetSpecialityName;
  if (ref.status) payload.status = ref.status;
  if (ref.rootId) payload.rootId = ref.rootId;

  return {
    order: {
      id: ref.id ?? fallbackId,
      type: 'referral',
      label: payload.target ?? payload.targetDisplay ?? '',
    },
    payload,
  };
}

/**
 * Build a vaccination PlanOrder + payload from one nested wire entry. The payload carries the FULL
 * entry verbatim so it round-trips losslessly (lot/expiry/site/route/dose/orderId/admin-requested
 * survive a re-save) — this is what stops a canvas autosave from wiping nested vaccinations. The
 * label is the vaccine display.
 */
function fromVaccination(
  v: AssessmentPlanVaccination,
  fallbackId: string,
): { order: HydratedOrder; payload: CodedOrderPayload } {
  return {
    order: { id: v.identifier ?? fallbackId, type: 'vaccination', label: v.vaccine?.display ?? '' },
    payload: { kind: 'vaccination', entry: v },
  };
}

/**
 * Rebuild the canvas state (DxCards + side-maps + identifier) from a fetched PatientAssessment.
 * Inverse of {@link toPatientAssessment}. Null/empty input → empty canvas.
 */
/**
 * Content identity for fan-in matching. The wire gives each NESTING its own row id, so id is useless
 * for recognising "the same order under another diagnosis" — we key on type + primary code, falling
 * back to the label when a payload carries no code. Mirrors Flutter, which dedupes on description.
 */
function orderIdentityKey(order: Omit<PlanOrder, 'dxIds'>, payload: CodedOrderPayload): string {
  // Payload shapes differ per kind: most carry `code`, medication carries `medicationCode`.
  const coded = payload as {
    code?: { code?: string | null } | null;
    medicationCode?: { code?: string | null } | null;
  };
  const code = coded.code?.code ?? coded.medicationCode?.code ?? null;
  const discriminator = code != null && code !== '' ? code : order.label.trim().toLowerCase();
  return `${order.type}::${discriminator}`;
}

export function patientAssessmentToCanvas(
  pa: PatientAssessment | null | undefined,
): PatientAssessmentCanvasState {
  const payloads: OrderPayloadMap = new Map();
  const dxCoding: DxCodingMap = new Map();

  if (!pa || !pa.assessmentPlanCondition || pa.assessmentPlanCondition.length === 0) {
    // Articles are parsed INDEPENDENTLY of diagnoses (Flutter patient_assessment_model.dart:194-197) —
    // an education-only assessment, or one whose diagnoses were removed while articles remain, must
    // still load its articles here, else the next save would re-emit assessmentPlanArticles: [].
    return {
      diagnoses: [],
      orders: [],
      payloads,
      dxCoding,
      // The narrative survives a diagnosis-less assessment for the same reason articles do: it is
      // top-level, so returning '' here would let the next save wipe a note the clinician wrote.
      assessmentNote: (pa?.notePlainText ?? pa?.notes ?? '') as string,
      assessmentNoteDelta: (pa?.notes ?? undefined) as string | undefined,
      articles: assessmentArticlesToEducation(pa?.assessmentPlanArticles),
      identifier: pa?.identifier ?? null,
    };
  }

  // Track which Dx ids exist so a top-level referral's parentId can resolve (fall back to the first).
  const dxIds = new Set<string>();
  // MULTI-Dx (PROD-1422): the BE stores multiple Dx links for MEDICATIONS ONLY (rxDiagnosisCodesRequest[],
  // keyed by dxCode.code). Map each Dx's conditionCode.code → its id up-front so a med can rehydrate its
  // full `dxIds` set (see medDxIds). All other order types are single-link by BE contract (parentId).
  // Track codes shared by >1 Dx: such a code can't reliably identify a Dx on reload (Codex Major), so
  // medDxIds must NOT use it to add a cross-link. Blank codes are skipped entirely (never keyed).
  const codeToDxId = new Map<string, string>();
  const ambiguousCodes = new Set<string>();
  for (const plan of pa.assessmentPlanCondition) {
    const code = (plan.conditionCode ?? plan.externalCode)?.code;
    if (code == null || code === '') continue; // blank code never identifies a Dx
    if (codeToDxId.has(code)) ambiguousCodes.add(code);
    else codeToDxId.set(code, plan.identifier ?? '');
  }
  // Flat top-level order list (v2).
  //
  // FAN-IN (PROD-1801, 2026-07-30) — the inverse of the save mapper's fan-out. One clinical order
  // linked to N diagnoses is stored as N nested entries, so a naive hydrate produced N SEPARATE
  // orders each with a single link. Worse, the server mints a DISTINCT row id per nesting (captured
  // on dev 2026-07-30: the same lab came back as 748c4129... under S32.464K and 2adec1c8... under
  // H31.003), so the copies cannot be recognised by id — they must be matched on CONTENT. Flutter
  // does the same, deduping on the order's description.
  //
  // So: the FIRST copy creates the order; every later copy just contributes its parent to `dxIds`.
  // Meds use this path too (PROD-1428): they fan out on save like labs, so a med's extra links come
  // back here; its first copy may also seed links from rxDiagnosisCodesRequest. Referrals are
  // top-level, so they don't rely on this path.
  const orders: PlanOrder[] = [];
  const byIdentity = new Map<string, PlanOrder>();
  const pushOrder = (
    hydrated: { order: Omit<PlanOrder, 'dxIds'>; payload: CodedOrderPayload },
    parentDxId: string,
    dxIdsOverride?: string[],
  ) => {
    const key = orderIdentityKey(hydrated.order, hydrated.payload);
    const existing = byIdentity.get(key);
    if (existing) {
      // Same clinical order under another diagnosis — widen the link set, don't duplicate the row.
      if (!existing.dxIds.includes(parentDxId)) existing.dxIds.push(parentDxId);
      return;
    }
    const linked = dxIdsOverride && dxIdsOverride.length > 0 ? dxIdsOverride : [parentDxId];
    const order: PlanOrder = { ...hydrated.order, dxIds: linked };
    byIdentity.set(key, order);
    orders.push(order);
    payloads.set(order.id, hydrated.payload);
  };

  // RESURRECTION GUARD (PROD-1801, 30 Jul). Soft-deleted rows were hydrated as LIVE and then re-saved
  // un-deleted, because the save mapper hardcodes `isDeleted: false` at every level. A resurrected
  // medication order is a prescribing event nobody intended. Only vaccinations were filtered; the
  // diagnosis itself and the other four order types were not.
  //
  // Same trade-off already accepted for vaccinations below: a soft-deleted row is DROPPED from the next
  // save rather than tombstoned. That is benign — the row was already deleted clinically — and it is
  // strictly better than reviving it.
  const notDeleted = <T extends { isDeleted?: boolean | null }>(row: T) => row.isDeleted !== true;

  // The A/P narrative. `notePlainText` FIRST: Flutter writes a Quill delta into `notes` and the
  // flattened text alongside it, so reading `notes` first would render raw JSON — and then re-save it.
  const assessmentNote = (pa.notePlainText ?? pa.notes ?? '') as string;
  // The delta half of the pair, kept raw. The editor parses it; a non-Delta legacy string is
  // tolerated there (it falls back to seeding from the plain text).
  const assessmentNoteDelta = (pa.notes ?? undefined) as string | undefined;

  const diagnoses: DxCard[] = pa.assessmentPlanCondition.filter(notDeleted).map((plan) => {
    const dxId = plan.identifier ?? '';
    dxIds.add(dxId);
    const coding = plan.conditionCode ?? plan.externalCode ?? null;
    if (coding) {
      const display = coding.display ?? coding.Definition ?? '';
      dxCoding.set(dxId, {
        ...(coding.code != null ? { code: coding.code } : {}),
        display,
        ...(coding.system != null ? { system: coding.system } : {}),
      });
    }

    const cmc = plan.childMedicalCode;

    (cmc?.assessmentPlanMedication ?? []).filter(notDeleted).forEach((med, i) => {
      pushOrder(fromMedication(med, fallbackOrderId(dxId, 'med', i)), dxId, medDxIds(med, dxId, codeToDxId, ambiguousCodes));
    });
    (cmc?.radiologyOrderRequest ?? []).filter(notDeleted).forEach((rad, i) => {
      pushOrder(fromRadiology(rad, fallbackOrderId(dxId, 'rad', i)), dxId);
    });
    (cmc?.assessmentPlanProcedures ?? []).filter(notDeleted).forEach((proc, i) => {
      pushOrder(fromProcedurePlan(proc, 'cpt', fallbackOrderId(dxId, 'proc', i)), dxId);
    });
    (cmc?.assessmentPlanSnomed ?? []).filter(notDeleted).forEach((sno, i) => {
      pushOrder(fromProcedurePlan(sno, 'snomed', fallbackOrderId(dxId, 'snomed', i)), dxId);
    });
    (cmc?.assessmentPlanVaccination ?? []).filter(notDeleted).forEach((vax, i) => {
      // Skip soft-deleted — consistent with to-vaccination-requests (the nurse view deliberately
      // excludes them; showing a deleted administration is a defect). Trade-off: a soft-deleted
      // vaccination is then dropped from the doc on the next autosave rather than tombstoned —
      // benign (the row was already deleted clinically). Active rows round-trip verbatim.
      pushOrder(fromVaccination(vax, fallbackOrderId(dxId, 'vax', i)), dxId);
    });

    // RENDER name source = the one shared clinical formatter (PROD-1656 Phase A): for
    // today's wire this resolves the same `display` (falling through Definition) so the
    // canvas is pixel-identical, and it starts preferring the dictionary
    // `preferredDisplay` / FHIR `text` the moment the BE serves them. NOTE the
    // `dxCoding` side-map above deliberately does NOT route through the formatter —
    // it preserves the raw wire coding for the save round-trip (write-back must echo
    // what the server sent, never a resolved display).
    const card: DxCard = {
      id: dxId,
      ...(coding?.code != null ? { code: coding.code } : {}),
      display: clinicalDisplayName(coding).name,
      isPrimary: plan.isPrimary === true,
      // Carry the onset stamp so a re-save preserves it instead of nulling it (PROD-1801).
      ...(plan.onSetDate ? { onSetDate: plan.onSetDate } : {}),
      // …and its endDate, which Flutter's AssessmentPlan round-trips too (PROD-2854).
      ...(plan.endDate ? { endDate: plan.endDate } : {}),
    };
    return card;
  });

  // Top-level referrals re-attach to their parent Dx via parentId (fall back to the first card).
  const firstDxId = diagnoses[0]?.id;
  // …AND referrals, which the first pass missed. `AssessmentReferral` declares `isDeleted` and the
  // counting helper above already respects it, so a soft-deleted referral was being COUNTED as gone
  // while still hydrating as a live order — then re-saved un-deleted. Same resurrection defect as the
  // other five lists, just one level up the document (referrals are top-level, not under
  // childMedicalCode, which is how it escaped the sweep).
  (pa.referrals ?? []).filter(notDeleted).forEach((ref, i) => {
    const parentDxId =
      ref.parentId != null && dxIds.has(ref.parentId) ? ref.parentId : firstDxId;
    if (parentDxId == null) return; // no Dx to attach to (defensive; a referral needs a parent)
    pushOrder(
      fromReferral(ref, fallbackOrderId(parentDxId ?? ref.parentId ?? 'orphan', 'ref', i)),
      parentDxId,
    );
  });

  // PRIMARY-FIRST (review FIX 3): the wire carries isPrimary EXPLICITLY per condition and does NOT
  // guarantee the primary Dx is first in assessmentPlanCondition[]. The save mapper now writes
  // isPrimary from the FLAG (not position), so the flag is authoritative — but we still lead with the
  // primary Dx so the canvas renders it first (index badge 1) matching Flutter. No primary → as-is.
  // Index the FILTERED cards, not the raw wire array. Reading `pa.assessmentPlanCondition` here was a
  // latent misalignment introduced by the resurrection guard above: with a soft-deleted condition
  // sitting BEFORE the primary one, the wire index (say 1) overshoots the filtered `diagnoses`
  // (length 1), so `diagnoses[primaryIndex]` is `undefined` and gets spliced to the FRONT of the
  // canvas — an undefined diagnosis card. `diagnoses` already carries `isPrimary`, so deriving the
  // index from it is both correct and self-aligning.
  const primaryIndex = diagnoses.findIndex((d) => d.isPrimary);
  const orderedDiagnoses =
    primaryIndex > 0
      ? [diagnoses[primaryIndex], ...diagnoses.filter((_, i) => i !== primaryIndex)]
      : diagnoses;

  // Legacy/blank documents may carry NO isPrimary anywhere (or, defensively, more than one). Since
  // the save mapper serializes the explicit flag, coerce to exactly one primary on load so the
  // invariant always holds (Flutter: "one diagnosis must be Primary"). (Codex/bot #147)
  return {
    diagnoses: ensureOnePrimary(orderedDiagnoses),
    orders,
    payloads,
    dxCoding,
    articles: assessmentArticlesToEducation(pa.assessmentPlanArticles),
    identifier: pa.identifier ?? null,
    assessmentNote,
    assessmentNoteDelta,
  };
}
